/*@jsxRuntime classic @jsx React.createElement @jsxFrag React.Fragment*/
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import React from "react";
import ExampleQuery from '../../components/ExampleQuery';
import JsonObjectPath from '../../components/JsonObjectPath';
import Layout from '../../components/Markdown';
const MDXLayout = Layout;
function _createMdxContent(props) {
  const _components = Object.assign({
    h1: "h1",
    a: "a",
    span: "span",
    p: "p",
    h2: "h2",
    strong: "strong",
    code: "code"
  }, _provideComponents(), props.components);
  return React.createElement(React.Fragment, null, React.createElement(_components.h1, {
    id: "importing-contact-lens-prescriptions"
  }, "Importing Contact Lens Prescriptions", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#importing-contact-lens-prescriptions"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "Importing contact lens prescriptions requires both the customer and member of staff to have an account in Starfish.\nStaff accounts must be added through the Starfish application, but customer accounts can be created via the API. We\nalso require information about the product and the prescribing location. The steps below walk through fetching all\nthe required data before importing the contact lens prescription."), "\n", React.createElement(_components.h2, {
    id: "1-fetch-the-staff-member-from-starfish"
  }, "1. Fetch the staff member from Starfish", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#1-fetch-the-staff-member-from-starfish"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "Query the staffMember endpoint to get the staff member's Starfish ID. We can use this ID when the member of staff\nimports contact lens prescriptions via the API."), "\n", React.createElement(ExampleQuery, {
    name: "queryStaffMember",
    limitHeight: true
  }), "\n", React.createElement(_components.h2, {
    id: "2-fetch-the-customer-from-starfish"
  }, "2. Fetch the customer from Starfish", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#2-fetch-the-customer-from-starfish"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "If there is not already an account holder for this customer, follow the documentation ", React.createElement(_components.a, {
    href: "/types/Mutation/createAccountHolder"
  }, "here"), "\nto create one."), "\n", React.createElement(_components.p, null, "Query the AccountHolder endpoint to get the customer's Starfish ID. We'll pass this up in the request when importing\nthe customer's prescription."), "\n", React.createElement(ExampleQuery, {
    name: "queryAccountHolder",
    limitHeight: true
  }), "\n", React.createElement(_components.h2, {
    id: "3-resolve-the-patient"
  }, "3. Resolve the patient", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#3-resolve-the-patient"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "Prescriptions are created against a ", React.createElement(_components.strong, null, "patient"), " — you pass ", React.createElement(_components.code, null, "patientId"), ", not the account\nholder's ID. Query the account holder's patients and pick the one the prescription is for — the\n", React.createElement(_components.code, null, "account_holder"), " patient for the customer themselves, or a dependant matched on\n", React.createElement(_components.code, null, "pmsPatientIdentifier"), "."), "\n", React.createElement(ExampleQuery, {
    name: "queryPatients",
    limitHeight: true
  }), "\n", React.createElement(_components.p, null, "If the prescription is for a dependant who does not exist yet, create them first. See\n", React.createElement(_components.a, {
    href: "/scenarios/patients-and-account-holders"
  }, "Patients and Account Holders"), " for the full\nexplanation of how patients relate to account holders."), "\n", React.createElement(_components.h2, {
    id: "4-fetch-the-product-from-starfish"
  }, "4. Fetch the product from Starfish", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#4-fetch-the-product-from-starfish"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "If we don't have the ID for the product, we can fetch that from the API."), "\n", React.createElement(ExampleQuery, {
    name: "queryProduct",
    limitHeight: true
  }), "\n", React.createElement(_components.h2, {
    id: "5-fetch-the-valid-properties-for-the-product-optional"
  }, "5. Fetch the valid properties for the product (optional)", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#5-fetch-the-valid-properties-for-the-product-optional"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "Each eye's ", React.createElement(_components.code, null, "leftEye"), "/", React.createElement(_components.code, null, "rightEye"), " input takes a list of ", React.createElement(_components.code, null, "{name, value}"), " properties (e.g. base curve, diameter,\npower) describing the specific variant of the product being prescribed. Only send the properties that apply to\nthis product - a spherical lens might only use ", React.createElement(_components.code, null, "power"), ", ", React.createElement(_components.code, null, "bc"), " and ", React.createElement(_components.code, null, "dia"), ", while a toric lens also uses ", React.createElement(_components.code, null, "cyl"), " and\n", React.createElement(_components.code, null, "axis"), ". If you already know which properties this product uses, you can skip this step; otherwise query\n", React.createElement(_components.code, null, "availableProperties"), " on the product to see exactly what to send:"), "\n", React.createElement(ExampleQuery, {
    name: "queryProductAvailableProperties",
    limitHeight: true
  }), "\n", React.createElement(_components.h2, {
    id: "6-fetch-the-location-from-starfish"
  }, "6. Fetch the location from Starfish", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#6-fetch-the-location-from-starfish"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "If the prescription is an in-house prescription, you can pass this ID up when importing the customer's prescription.\nIf you don't have this location's Starfish ID, you can fetch it from the API:"), "\n", React.createElement(ExampleQuery, {
    name: "queryLocation",
    limitHeight: true
  }), "\n", React.createElement(_components.h2, {
    id: "7-import-the-contact-lens-prescription"
  }, "7. Import the contact lens prescription", React.createElement(_components.a, {
    "aria-hidden": "true",
    tabIndex: "-1",
    href: "#7-import-the-contact-lens-prescription"
  }, React.createElement(_components.span, {
    className: "icon icon-link"
  }))), "\n", React.createElement(_components.p, null, "Now we have the patient's ID, the staff member's ID, the product ID and its properties, and the location ID, we can\nimport the prescription via the API following the documentation ", React.createElement(_components.a, {
    href: "/types/Mutation/createContactLensPrescription"
  }, "here"), "."));
}
function MDXContent(props = {}) {
  return React.createElement(MDXLayout, props, React.createElement(_createMdxContent, props));
}
export default MDXContent;
